import { LiveAnnouncer } from '@angular/cdk/a11y';
import { AfterViewInit, Component, Inject, LOCALE_ID } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../api/api.service';
import { FeatureSetField, CreatePasswordFeatureSetFields, EnterPasswordFeatureSetFields } from '../backend-types/feature-set';
import { resetPasswordRoute, verifyMFARoute, welcomeRoute } from '../route-names';
import { ApiAboutService } from '../Services/api-about.service';
import { DialogService, DialogType } from '../Services/dialog.service';
import { ElectorDataService } from '../Services/elector-data.service';
import { SessionService, csrfTokenHeaderName } from '../Services/session.service';
import { VerifyWithMfaResponse } from './verify-voter.component';

@Component({
    selector: 'app-password',
    templateUrl: './password.component.html',
    styleUrl: './password.component.scss',
    standalone: false
})
export class PasswordComponent implements AfterViewInit {
  loginForm: FormGroup = new FormGroup({});
  fields: FeatureSetField[] = [];
  dialogText?: string;
  dialogTitle?: string;
  hide: boolean = true;
  electionOpen: boolean = true;
  readonly electionStartTime: Date;
  readonly electionEndTime: Date;
  passwordRequirements = {
    hasNumber: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasSpecialChar: false,
    isValidLength: false,
  };

  voter_id = "email@address";

  screenType: 'NEW' | 'CHECK' = 'NEW';

  displaySpinner = false;
  
  constructor(
    private readonly dialog: MatDialog,
    readonly aboutService: ApiAboutService,
    private readonly fb: FormBuilder,
    public readonly announcer: LiveAnnouncer,
    @Inject(LOCALE_ID) readonly locale: string,
    private readonly router: Router,
    readonly sessionService: SessionService,
    private readonly apiService: ApiService,
    private dialogService: DialogService,
    readonly userService: ElectorDataService
  ) {
    if(this.aboutService.registrationNotYetOpen){ 
      this.dialogTitle = $localize`Election Not Open Yet`;
      this.dialogText = $localize`The election will open at ${this.aboutService.startTime}.`;
      this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogText);
      this.electionOpen = false;
    } 
    if(this.aboutService.electionNotYetOpen){ 
      this.dialogTitle = $localize`Election Not Open Yet`;
      this.dialogText = $localize`The election will open at ${this.aboutService.startTime}.`;
      this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogText);
      this.electionOpen = false;
    } 
    if(this.aboutService.electionClosed){
      this.dialogTitle = $localize`Election Closed`;
      this.dialogText = $localize`The election was open from ${this.aboutService.startTime} to ${this.aboutService.endTime}.`;
      this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
      this.electionOpen = false;
    }
    this.electionStartTime = new Date(2000, 1, 1);
    let start: string | undefined = this.aboutService.aboutInformation.value.org_feature_set.miscSettings?.electionStartTimestamp;
    if (start) {
      this.electionStartTime = new Date(start);
    }
    // I doubt anyone will use this after the year 100,000. 
    this.electionEndTime = new Date(100000, 1, 1);
    let expiry: string | undefined = this.aboutService.aboutInformation.value.org_feature_set.miscSettings?.electionEndTimestamp;
    if (expiry) {
      this.electionEndTime = new Date(expiry);
    }
    if(this.sessionService.partiallyDeletedCookies()){
      try {
        let xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/endsession');
        xhr.withCredentials = true;
        if(this.sessionService.xsrfCookieName) {
          xhr.setRequestHeader(csrfTokenHeaderName, this.sessionService.xsrfCookieName);
        }
        xhr.send();
      } catch(e) {
        console.error("Error calling /endsession.")
      }
    } else {
      if(this.sessionService.activeSessionCheck()) {
        this.dialogTitle = $localize`Active Session`;
        this.dialogText = $localize`Someone is already logged in on this browser. Please close this tab and log in on another browser or another device.`;
        this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
      }
    }
    this.screenType = this.router.getCurrentNavigation()?.extras.state?.type ?? 'NEW';
    this.voter_id = this.router.getCurrentNavigation()?.extras.state?.voterID ?? '';
    if(this.screenType === 'NEW')  {
      this.fields = CreatePasswordFeatureSetFields;
    } else if (this.screenType === 'CHECK') {
      this.fields = EnterPasswordFeatureSetFields;
    } else {
      this.router.navigate(verifyMFARoute, {state: {voterID: this.voter_id}});
    }
    this.loginForm = new FormGroup({});
    this.addFormValidators();
  }

  ngAfterViewInit() {
    this.loginForm.get('Email')?.patchValue(this.voter_id);
  
    const passwordControl = this.loginForm.get('Password');
    if (passwordControl) {
      passwordControl.valueChanges.subscribe((value: string) => {
        this.validatePassword(value);
      });
    } else {
      console.error('Password control not found');
    }
  }
  
  private addFormValidators(): void {
    for (let field of this.fields) {
      let validators: ValidatorFn[] = [];
      if (field.isMandatory) {
        validators.push(Validators.required);
      }
      if (field.fieldType === 'password') {
        validators.push(Validators.pattern(pwRegex));
      }
      if (field.fieldType === 'confirm_password') {
        validators.push(this.validateConfirmPassword.bind(this));
      }
      this.loginForm.addControl(field.fieldName, this.fb.control(null, validators));
    }
    // One more pass to cross-check the confirming fields.
    for (let field of this.fields) {
      if (field.fieldType === 'password') {
        const confirmPasswordField = this.fields.find(f =>
          f.fieldType === 'confirm_password'
        );
        if (confirmPasswordField) {
          this.loginForm.get(field.fieldName)?.valueChanges.subscribe(() => {
            this.loginForm.get(confirmPasswordField.fieldName)?.updateValueAndValidity();
          });
        }
      }
    }
  }

  private validatePassword(password: string): void {
    this.passwordRequirements.hasNumber = /\d/.test(password);
    this.passwordRequirements.hasUpperCase = /[A-Z]/.test(password);
    this.passwordRequirements.hasLowerCase = /[a-z]/.test(password);
    this.passwordRequirements.hasSpecialChar = /[^a-zA-Z\d]/.test(password);
    this.passwordRequirements.isValidLength = (password.length >= 12) && (password.length <= 1024);
  }

  private validateConfirmPassword(control: AbstractControl): ValidationErrors | null {
    const password = control.parent?.get('Password');
    const confirmPassword = control.parent?.get('Confirm_Password');
    return password?.value == confirmPassword?.value ? null : { 'PasswordNoMatch': true };
  }
  
  onTooltipClick(event: any) {
    event.preventDefault();
  }

  async onSubmit() {
    if(this.sessionService.activeSessionCheck()) {
      this.dialogTitle = $localize`Active Session`;
      this.dialogText = $localize`Someone is already logged in on this browser. Please close this tab and onboard on another browser or another device.`;
      this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
      return;
    } 
    let currentTime: Date = new Date(Date.now());
    if (currentTime > this.electionEndTime) {
      this.dialogTitle = $localize`Election Closed`;
      this.dialogText = $localize`The election was open from ${this.aboutService.startTime} to ${this.aboutService.endTime}.`;
      this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
      return;
    }
    if(this.loginForm.invalid) {
      this.dialogTitle = $localize`Invalid Password`;
      this.dialogText = $localize`Your password did not meet one of the requirements.`;
      this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
      return;
    }
    this.displaySpinner = true;

    const requestBody = {
      voter_id: this.voter_id,
      password: this.loginForm.get('Password')?.value,
      password_screen: this.screenType,
      locale: this.locale
    };

    try {
      let response = await firstValueFrom(this.apiService.postWithHandshakeCheck<VerifyWithMfaResponse>('/api/login', requestBody));
      this.displaySpinner = false;
      if(response.body){
        this.userService.userAuthData.set({
          voter_id: response.body?.voter_id,
          mfaMethod: response.body?.mfa_method,
          mfaVerified: false,
          mfaExpiration: response.body?.mfa_expiration,
          mfaExpirationTime: response.body?.mfa_expiration_time,
          maxResends: response.body?.max_resends,
          resendsRemaining: response.body?.resends_remaining,
          resendExpiration: response.body?.resend_expiration,
          resendExpirationTime: response.body?.resend_expiration_time,
          maxAttempts: response.body?.max_attempts,
          attemptsRemaining: response.body?.attempts_remaining
        });
        this.dialogTitle = $localize`Success!`;
        if(this.screenType === 'NEW'){
          this.dialogText = $localize`You have successfully created a password. ` + $localize`You will now verify your email address.`;
          this.dialogService.openDialog(DialogType.SUCCESS, this.dialogTitle, this.dialogText);
          setTimeout(() => {
            this.dialog.closeAll();
            this.router.navigate(verifyMFARoute);
          }, 2000);
        } else {
          this.router.navigate(verifyMFARoute);
        }
      } else {
        this.dialogText = $localize`An unknown error occurred. Please consult the Help menu for assistance.`
        this.dialogTitle = $localize`Unknown error.`
        this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
      }
    } catch (err: any) {
      this.displaySpinner = false;
      if ((err.error_type !== undefined && err.error_type === 'ALREADY_LOGGED_IN') || (err.error !== undefined && err.error.error_type === 'ALREADY_LOGGED_IN')) {
        this.dialogText = $localize`You already have an active session. To continue, please close the active session and try again.`;
        this.dialogTitle = $localize`Already Logged In.`
        this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
      } else if ((err.error_type !== undefined && err.error_type === 'INVALID_IDENTITY') || (err.error !== undefined && err.error.error_type === 'INVALID_IDENTITY')) {
        if(this.userService.userAuthData()) {
          this.userService.userAuthData()!.attemptsRemaining = (this.userService.userAuthData()!.attemptsRemaining ?? 5) - 1; 
        }
        this.dialogText = $localize`The information provided does not match our records. Please verify that you have entered the information correctly. If you believe this is in error, use the "Contact Support" link for assistance.`;
        this.dialogTitle = $localize`Information not found.`;
        this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
      } else if ((err.error_type !== undefined && err.error_type === 'INVALID_USERNAME_OR_PASSWORD') || (err.error !== undefined && err.error.error_type === 'INVALID_USERNAME_OR_PASSWORD')) {
        if(this.userService.userAuthData()) {
          this.userService.userAuthData()!.attemptsRemaining = (this.userService.userAuthData()!.attemptsRemaining ?? 5) - 1; 
        }
        this.dialogText = $localize`The information provided does not match our records. Please verify that you have entered the information correctly. If you believe this is in error, use the "Contact Support" link for assistance.`;
        this.dialogTitle = $localize`Information not found.`;
        this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
      } else if ((err.error_type !== undefined && err.error_type === 'UNKNOWN_USER') || (err.error !== undefined && err.error.error_type === 'UNKNOWN_USER')) {
        if(this.userService.userAuthData()) {
          this.userService.userAuthData()!.attemptsRemaining = (this.userService.userAuthData()!.attemptsRemaining ?? 5) - 1; 
        }
        this.dialogText = $localize`The information provided does not match our records. Please verify that you have entered the information correctly. If you believe this is in error, use the "Contact Support" link for assistance.`;
        this.dialogTitle = $localize`Information not found.`;
        this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogText);
      } else if ((err.error_type !== undefined && err.error_type === 'MAX_OTP_ATTEMPTS') || (err.error !== undefined && err.error.error_type === 'MAX_OTP_ATTEMPTS')) {
        this.dialogText = $localize`Your account has been temporarily blocked. To unblock it, please use the "Contact Support" link for assistance.`;
        this.dialogTitle = $localize`Maximum Attempts Reached.`
        this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
        this.router.navigate(welcomeRoute);
      } else if ((err.error_type !== undefined && err.error_type === 'ACCOUNT_LOCKED') || (err.error !== undefined && err.error.error_type === 'ACCOUNT_LOCKED')) {
        if(this.userService.userAuthData()) {
          this.userService.userAuthData()!.attemptsRemaining = 0; 
        }
        this.dialogText = $localize`Your account has been locked. Please consult the Help menu for assistance.`;
        this.dialogTitle = $localize`Locked Account`
        this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
      } else if ((err.error_type !== undefined && err.error_type === 'INVALID_FORM') ||
      (err.error !== undefined && err.error.error_type === 'INVALID_FORM')) {
        this.dialogText = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
        this.dialogTitle = $localize`Malformed Data`
        this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogText);
      } else if ((err.error_type !== undefined && (err.error_type === 'DECODER_STATE_LOST' || err.error_type === 'INVALID_MTE_DECODING')) ||
        (err.error !== undefined && (err.error.error_type === 'DECODER_STATE_LOST' || err.error.error_type === 'INVALID_MTE_DECODING' ))) {
        const errorText = $localize`We could not process your request. Please try to submit again or refresh the page.`;
        const errorTitle = $localize`Error.`
        this.dialogService.openDialog(DialogType.ALERT, errorTitle, errorText);
      } else {
        this.dialogText = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`
        this.dialogTitle = $localize`Error`
        this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
      }
    }
  }

  forgotPassword() {
    this.router.navigate(resetPasswordRoute, {state: {voterID: this.voter_id}});
  }

}

export const pwRegex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?\d)(?=.*?[^a-zA-Z\d]).{12,1024}$/;